import { Injectable } from '@angular/core';
import { IProject } from '@common/interfaces/project';
import { Topics } from '@common/interfaces/topics';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ActionService } from '../action/action.service';
import { CrudService } from '../crudService';
import { LastUpdateService } from '../lastUpdate/lastUpdate.service';
import { ProjectStoreStrategy } from '../updateStoreStrategies';
import { ProjectQuery } from './project.query';
import { ProjectStore } from './project.store';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ProjectService extends CrudService<IProject,ProjectStore,ProjectQuery> {

  constructor(
    public store: ProjectStore,
    public query: ProjectQuery,
    protected actionService: ActionService,
    protected updateStrategy: ProjectStoreStrategy,
    private lastUpdateService: LastUpdateService
  ) {
    super(
      Topics.PROJECTS,
      store,
      query,
      actionService,
      updateStrategy
    );
  }

  firstSync$ = this.updateStoreStrategy.firstSync$;

  setActive(id: string) {
    this.resetBadges(id);
    this.store.setActive(id);
  }

  reloadProject(projectId: string) {
    this.lastUpdateService.cleanChildLastUpdate(projectId);
    if (this.query.getActiveId() !== projectId) {
      return;
    }
    this.setActive(null);
    this.setActive(projectId);
  }

  getActive$(): Observable<IProject> {
    return this.query.active$;
  }

  getActive(): IProject {
    return this.query.getActive();
  }

  addBadges(projectId: string, news: string[]): void {
    const active = this.getActive();
    if(!active || active.id !== projectId) {
      this.store.ui.update(projectId as string, ui => { 
        const newsBadge = new Set<string>([...news, ...ui.newsBadge]);
        return {...ui, newsBadge: [...newsBadge.values()]} 
      });
    }
  }

  resetBadges(projectId: string): void { 
    if(!projectId) return;
    this.store.ui.update(projectId as string, ui => { return {...ui, newsBadge: []} });
  }

  getBadgeNews(projectId: string): number {
    return this.query.ui.getEntity(projectId as string).newsBadge.length;
  }

  getBadgeNews$(projectId: string): Observable<number> {
    return this.query.ui.selectEntity(projectId as string).pipe(
      filter(projectUi => !!projectUi),
      map(projectUi => {
        const active = this.getActive();
        if(!active || projectUi.id !== active.id) {
          return projectUi.newsBadge.length;
        } else {
          return 0;
        }
      }),
      distinctUntilChanged(),
    );
  }

  updateProject(id: string, updates: Partial<IProject>) {
    const prevProject = this.query.getEntity(id);
    let payload = {...updates, id} as Partial<IProject>;
    this.store.upsert(id, payload);
    this.actionService.queueAction({ topic: this.topic, type: "update", payload, backup: prevProject })
  }

}
